import { useMemo } from 'react';
import { Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { djs } from '@duke-hq/djs';

import { CalendarClock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

import ContentContainer from './shared/ContentContainer';
import EnrolmentCard from './shared/EnrolmentCard';
import SidePanelContentHeader from './shared/ContentHeader';
import NoDataMessage from './shared/NoDataMessage';

function RevisionCardFooter({
  totalAnswers,
  incorrectAnswers,
  courseEnrolmentId,
}: {
  totalAnswers: number;
  incorrectAnswers: number;
  courseEnrolmentId: number;
}) {
  const textColor = incorrectAnswers / totalAnswers > 0.5 ? 'danger' : 'success';

  return (
    <>
      <HStack spacing="4px" alignItems="center">
        <Text fontSize="xs" fontWeight="medium" color={textColor}>
          {incorrectAnswers}/{totalAnswers}
        </Text>

        <Text fontSize="xs" fontWeight="medium" color="fg.subtle">
          <Trans>requires revision</Trans>
        </Text>
      </HStack>
      <Button
        to={`/enrolment/${courseEnrolmentId}`}
        as={Link}
        size="2xs"
        variant="ghost"
        colorScheme="whiteAlpha"
        color="fg.subtle"
        fontSize="xs"
        rightIcon={<ExternalLink size="14" />}
      >
        <Trans>View Enrolment</Trans>
      </Button>
    </>
  );
}

type Props = {
  timeframe?: 'ALL' | 'THIS_WEEK' | 'OVER_ONE_WEEK';
};

function RevisionsWithData({ timeframe = 'ALL' }: Props) {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getRevisionsRequested.useQuery(
    { filters, timeframe },
    { suspense: true },
  );

  const { title, tooltip } = useMemo(() => {
    switch (timeframe) {
      case 'ALL':
        return {
          title: t`All`,
          tooltip: t`All outstanding assessment revisions`,
        };
      case 'THIS_WEEK':
        return {
          title: t`This week`,
          tooltip: t`Outstanding assessment revisions requested this week`,
        };
      case 'OVER_ONE_WEEK':
        return {
          title: t`Over one week`,
          tooltip: t`Outstanding assessment revisions requested over one week ago`,
        };
    }
  }, [timeframe]);

  if (!data || data.length === 0) {
    return <NoDataMessage />;
  }

  return (
    <>
      <SidePanelContentHeader title={title} tooltipText={tooltip} />

      <VStack>
        {data.map((assessment) => (
          <EnrolmentCard
            key={assessment.courseEnrolmentId}
            studentName={assessment.studentName}
            courseTitle={assessment.unitOfCompetencyTitle}
            courseIdentifier={assessment.unitOfCompetencyCode}
            headerText={
              assessment.revisionRequestedDate !== null
                ? djs(assessment.revisionRequestedDate).fromNow()
                : ''
            }
            HeaderIcon={CalendarClock}
            Footer={
              <RevisionCardFooter
                totalAnswers={assessment.totalAnswers}
                incorrectAnswers={assessment.incorrectAnswers}
                courseEnrolmentId={assessment.courseEnrolmentId}
              />
            }
          />
        ))}
      </VStack>
    </>
  );
}

export default function Revisions(props: Props) {
  return (
    <ContentContainer title={t`Revisions requested`}>
      <RevisionsWithData {...props} />
    </ContentContainer>
  );
}
